import React, { FC, ReactNode, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Tabs, IconTypesKeys, ThemeName } from "@evergis/ui";
import { ResourceTypeLink } from "@evergis/api";

import { LocalizationMenu } from "../../../../components/LocalizationMenu/LocalizationMenu";
import { useResourceSelectorContext } from "../../../../components/ResourceSelector/ResourceSelector";
import { getLocationQuery } from "../../../../ducks/location/selectors";

import {
  CloseButton,
  Container,
  DialogHeader,
  HeaderBlock,
  LocalizationMenuContainer,
  RightButtonsContainer,
  ShadowContainer,
  TabsContainer,
  TopContainer,
} from "./styled";

import { useResourceType } from "../../../../hooks/useResourceType";
import { useSelectedResetEffect } from "../../hooks/useSelectedResetEffect";
import { useViewType } from "../../hooks/useViewType";
import { useKey } from "../../../../hooks/useKey";
import { goToMainBase } from "../../../../ducks/location/location";
import { getProjectName } from "../../../../ducks/project/selectors";
import { getSettingsProp } from "../../../../ducks/appConfig";
import { getThemeName } from "../../../../ducks/theme/selectors";

import { ResourceViewType } from "../ViewType/types";
import { RoutePanelName } from "../../../../appConfig/types";

interface CatalogHeaderProps {
  children: ReactNode;
}

export const CatalogHeader: FC<CatalogHeaderProps> = ({ children }) => {
  const { t } = useTranslation("catalog");
  const dispatch = useDispatch();
  const currentProject = useSelector(getProjectName);
  const themeName = useSelector(getThemeName);
  const localization = useSelector(getSettingsProp("localization"));
  const [resourceType, updateResourceType] = useResourceType();
  const [viewType] = useViewType();
  const { addFromCatalog, openMap, addAsBasemap } = useSelector(getLocationQuery);
  const {
    active: resourceSelectorActive,
    resourceType: selectorResourceType,
    onClose: onResourceSelectorClose,
  } = useResourceSelectorContext();

  const selectTypeTexts = useMemo(
    () => ({
      [ResourceTypeLink.Project]: t("map1"),
      [ResourceTypeLink.Layer]: t("layer1"),
      [ResourceTypeLink.Table]: t("table1"),
    }),
    [t],
  );

  const tabs = useMemo(
    () =>
      [
        !addFromCatalog &&
          !addAsBasemap && {
            value: ResourceTypeLink.Project,
            text: t("maps"),
            icon: "map" as IconTypesKeys,
          },
        !openMap &&
          !addAsBasemap && {
            value: ResourceTypeLink.Layer,
            text: t("layers"),
            icon: "layers" as IconTypesKeys,
          },
        !openMap &&
          !addAsBasemap && {
            value: ResourceTypeLink.Table,
            text: t("tables"),
            icon: "data" as IconTypesKeys,
          },
      ].filter(Boolean),
    [t, addFromCatalog, openMap, addAsBasemap],
  );

  useSelectedResetEffect(resourceType);

  const closeCatalog = useCallback(() => {
    if (onResourceSelectorClose) {
      onResourceSelectorClose();
    } else if (currentProject) {
      dispatch(goToMainBase({ project: currentProject, panel: RoutePanelName.Layers }));
    }
  }, [currentProject, dispatch, onResourceSelectorClose]);

  useKey({
    Escape: closeCatalog,
  });

  const ContentContainer = viewType === ResourceViewType.Table ? Container : ShadowContainer;

  return (
    <ContentContainer width={"100%"}>
      <HeaderBlock $isDark={themeName === ThemeName.Dark} dialogStyled>
        {currentProject && <CloseButton title={t("closeCatalog")} kind={"close"} onClick={closeCatalog} />}
        {addFromCatalog && <DialogHeader>{t("addToMap")}</DialogHeader>}
        {addAsBasemap && <DialogHeader>{t("addBasemap")}</DialogHeader>}
        {openMap && <DialogHeader>{t("openMap")}</DialogHeader>}
        {resourceSelectorActive && (
          <DialogHeader>
            {t("actions.select", { ns: "common" })} {selectTypeTexts[selectorResourceType]}
          </DialogHeader>
        )}
        <TopContainer>
          <TabsContainer>
            <Tabs
              align={"left"}
              data={resourceSelectorActive ? [] : tabs}
              selectedTab={resourceType}
              onChange={updateResourceType}
            />
          </TabsContainer>
          {!currentProject && (
            <RightButtonsContainer>
              {localization && (
                <LocalizationMenuContainer>
                  <LocalizationMenu placement="left-start" />
                </LocalizationMenuContainer>
              )}
            </RightButtonsContainer>
          )}
        </TopContainer>
        {children}
      </HeaderBlock>
    </ContentContainer>
  );
};
